import { clsx } from "clsx";
import { Minus, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import {
  cloneElement,
  createContext,
  type FormEvent,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  EscapeLayerBoundary,
  useEscape,
  useFocusTrap,
  useId,
  useMediaQuery,
  useScrollLock,
} from "@/shared/lib/hooks";
import { t, useLocale } from "@/shared/lib/i18n";
import { OverlayVisibilityProvider, useOverlayVisibility } from "@/shared/lib/overlayVisibility";
import { useDockedWindow, useWindowDock } from "@/shared/ui/layout/WindowDock";
import { Form } from "@/shared/ui/primitives/Form";
import { Button, IconButton } from "./Button";

/**
 * Overlays.
 *
 * One family, three shapes: a centred dialog for a decision, a side sheet for
 * inspecting something without leaving the surface behind it, and a popover
 * for a short list of choices. Modal layers trap focus and lock page scrolling;
 * the non-modal popover does neither. Each layer owns Escape/focus restoration
 * and animates from its spatial origin — motion here is orientation, not
 * decoration.
 */

const BACKDROP = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.18 },
};

/**
 * Moves one layer to the document overlay root and declares it a nesting level.
 *
 * AppShell isolates its scrolling canvases, so a fixed overlay mounted inside
 * one of them can be painted over by shell chrome. Portalling fixes that but
 * costs the DOM nesting Escape used to read; the boundary restores it from the
 * React tree, which never lied about which layer opened which.
 */
const OverlayDepthContext = createContext(0);

function portalLayer(layer: ReactNode, visible: boolean, depth = 0) {
  const scoped = (
    <OverlayVisibilityProvider visible={visible}>
      <OverlayDepthContext.Provider value={depth + 1}>
        <EscapeLayerBoundary>{layer}</EscapeLayerBoundary>
      </OverlayDepthContext.Provider>
    </OverlayVisibilityProvider>
  );
  return typeof document === "undefined" ? scoped : createPortal(scoped, document.body);
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  placement = "responsive",
  layerOrder = 0,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  placement?: "responsive" | "center";
  layerOrder?: number;
}) {
  useLocale();
  const inheritedDepth = useContext(OverlayDepthContext);
  const overlayDepth = Math.max(inheritedDepth, layerOrder * 10);
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId("dialog-title");
  const descriptionId = useId("dialog-description");
  const visible = useOverlayVisibility() && open;
  useFocusTrap(panel, visible);
  useScrollLock(visible);
  useEscape(onClose, visible, panel, overlayDepth);

  const width = size === "sm" ? "max-w-md" : size === "lg" ? "max-w-3xl" : "max-w-xl";

  const layer = (
    <AnimatePresence>
      {open && (
        <div
          hidden={!visible}
          inert={!visible}
          style={{ display: visible ? undefined : "none", zIndex: 50 + overlayDepth }}
          className={clsx(
            "cx-overlay-viewport fixed z-50 flex justify-center",
            placement === "center" ? "items-center p-3 sm:p-4" : "items-end sm:items-center",
          )}
        >
          <motion.div
            {...BACKDROP}
            className="absolute inset-0 bg-overlay backdrop-blur-[2px]"
            onClick={onClose}
          />
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descriptionId : undefined}
            initial={{ opacity: 0, y: 12, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.99 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className={clsx(
              "relative flex max-h-[calc(var(--ex-viewport-height)*0.92)] w-full flex-col overflow-hidden bg-surface-raised",
              placement === "center"
                ? "rounded-2xl border border-line shadow-[var(--ex-shadow-overlay)]"
                : "rounded-t-2xl border border-line shadow-[var(--ex-shadow-overlay)] sm:rounded-2xl",
              width,
            )}
          >
            <header className="flex items-start justify-between gap-4 border-b border-line-soft px-5 py-4">
              <div className="min-w-0">
                <h2 id={titleId} className="text-lg font-medium text-ink">
                  {title}
                </h2>
                {description && (
                  <p id={descriptionId} className="mt-1 text-sm text-ink-muted">
                    {description}
                  </p>
                )}
              </div>
              <IconButton label={t("copy.7b31a9fc4816")} size="sm" onClick={onClose}>
                <X className="size-4" />
              </IconButton>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

            {footer && (
              <footer className="flex items-center justify-end gap-2 border-t border-line-soft bg-surface-muted px-5 py-3">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // AppShell deliberately isolates its scrolling canvases. Mounting a fixed
  // dialog inside one of those stacking contexts lets the mobile bottom bar
  // paint above the dialog footer even when the dialog itself has a larger
  // z-index. Canonical overlays belong to the document layer, not to the page
  // that happened to open them.
  return portalLayer(layer, visible, overlayDepth);
}

/**
 * Canonical create/edit form contract.
 *
 * Unlike an inspector Sheet, a record form is centred at every viewport and
 * owns one real HTML form. Its footer stays fixed while dense fields scroll;
 * a dirty close turns into an inline discard choice instead of nesting a
 * second modal on top of the first one.
 */
export type RecordDialogCloseController = Readonly<{
  open: boolean;
  dirty: boolean;
  requestClose: (afterClose?: () => void) => void;
}>;

type DetailEditorRegistration = (
  id: string,
  editor: (() => RecordDialogCloseController) | null,
) => void;
const DetailEditorContext = createContext<DetailEditorRegistration | null>(null);
const RecordDetailActionsContext = createContext<{
  minimize: () => void;
  requestClose: () => void;
} | null>(null);
export function useRecordDetailActions() {
  return useContext(RecordDetailActionsContext);
}

export function RecordDialog({
  open,
  onClose,
  onSubmit,
  title,
  description,
  children,
  size = "md",
  submitLabel = t("copy.c1fe2ecdff02"),
  cancelLabel = t("copy.39a8d28c1c6b"),
  pending = false,
  submitDisabled = false,
  dirty = false,
  presentation = "dialog",
  closeControllerRef,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  submitLabel?: string;
  cancelLabel?: string;
  pending?: boolean;
  submitDisabled?: boolean;
  dirty?: boolean;
  /**
   * A managed desktop window already owns its title bar, stacking and focus
   * boundary. Its record editor therefore stays inside that window instead of
   * opening a second modal above it. The ordinary route/page contract remains
   * the default.
   */
  presentation?: "dialog" | "embedded";
  /**
   * Lets a parent window route its own close affordances through this form's
   * dirty-discard contract. An optional continuation runs only after the form
   * really closes; choosing to keep editing clears it.
   */
  closeControllerRef?: Ref<RecordDialogCloseController>;
}) {
  useLocale();
  const formId = useId("record-form");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const afterCloseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (open) {
      afterCloseRef.current = null;
      setConfirmDiscard(false);
    }
  }, [open]);

  const completeClose = useCallback(() => {
    const afterClose = afterCloseRef.current;
    afterCloseRef.current = null;
    setConfirmDiscard(false);
    onClose();
    afterClose?.();
  }, [onClose]);

  const keepEditing = useCallback(() => {
    afterCloseRef.current = null;
    setConfirmDiscard(false);
  }, []);

  const requestClose = useCallback(
    (afterClose?: () => void) => {
      if (pending) return;
      if (confirmDiscard) {
        keepEditing();
        return;
      }
      if (dirty) {
        afterCloseRef.current = afterClose ?? null;
        setConfirmDiscard(true);
        return;
      }
      onClose();
      afterClose?.();
    },
    [confirmDiscard, dirty, keepEditing, onClose, pending],
  );

  useImperativeHandle(closeControllerRef, () => ({ open, dirty, requestClose }), [
    dirty,
    open,
    requestClose,
  ]);

  const registerEditor = useContext(DetailEditorContext);
  const editorState = useRef({ open, dirty, requestClose });
  editorState.current = { open, dirty, requestClose };
  useEffect(() => {
    // A parent with an explicit controller already owns this form's close continuation.
    if (!registerEditor || !open || closeControllerRef) return;
    registerEditor(formId, () => editorState.current);
    return () => registerEditor(formId, null);
  }, [closeControllerRef, formId, open, registerEditor]);

  const footer = confirmDiscard ? (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <p
        className="min-w-0 basis-full text-sm leading-relaxed text-caution sm:flex-1"
        role="status"
      >
        {" "}
        {t("copy.5914f47999f8")}{" "}
      </p>
      <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
        <Button size="sm" onClick={keepEditing}>
          {" "}
          {t("copy.7b294c635e76")}{" "}
        </Button>
        <Button size="sm" variant="danger" onClick={completeClose}>
          {" "}
          {t("copy.7696d78c4ec1")}{" "}
        </Button>
      </div>
    </div>
  ) : (
    <>
      <Button disabled={pending} onClick={() => requestClose()}>
        {cancelLabel}
      </Button>
      <Button
        type="submit"
        form={formId}
        variant="primary"
        loading={pending}
        disabled={submitDisabled}
      >
        {submitLabel}
      </Button>
    </>
  );

  if (presentation === "embedded") {
    return (
      <EmbeddedRecordForm
        open={open}
        onRequestClose={() => requestClose()}
        onSubmit={onSubmit}
        title={title}
        description={description}
        formId={formId}
        footer={footer}
      >
        {children}
      </EmbeddedRecordForm>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => requestClose()}
      title={title}
      description={description}
      size={size}
      placement="center"
      footer={footer}
    >
      <Form id={formId} onSubmit={onSubmit} noValidate>
        {children}
      </Form>
    </Dialog>
  );
}

function EmbeddedRecordForm({
  open,
  onRequestClose,
  onSubmit,
  title,
  description,
  formId,
  footer,
  children,
}: {
  open: boolean;
  onRequestClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  title: string;
  description?: ReactNode;
  formId: string;
  footer: ReactNode;
  children: ReactNode;
}) {
  useLocale();
  const overlayDepth = useContext(OverlayDepthContext);
  const ownerVisible = useOverlayVisibility();
  const visible = open && ownerVisible;
  const panel = useRef<HTMLElement>(null);
  const retainedFocus = useRef<HTMLElement | null>(null);
  const titleId = useId("embedded-record-title");
  const descriptionId = useId("embedded-record-description");

  useEffect(() => {
    if (!visible) return;
    const frame = requestAnimationFrame(() => {
      const owner = panel.current;
      if (!owner || owner.contains(document.activeElement)) return;
      const retained = retainedFocus.current;
      const firstField = owner.querySelector<HTMLElement>(
        '[autofocus], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"], button:not([disabled])',
      );
      const target = retained?.isConnected && owner.contains(retained) ? retained : firstField;
      target?.focus({ preventScroll: true });
      target?.scrollIntoView?.({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  if (!open) return null;

  return (
    <section
      ref={panel}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      hidden={!visible}
      inert={!visible}
      style={{ display: visible ? undefined : "none", zIndex: 50 + overlayDepth }}
      data-record-editor-embedded="true"
      className="flex max-h-[min(70dvh,48rem)] min-h-0 flex-col overflow-hidden rounded-xl border border-accent-line bg-surface-raised shadow-[var(--ex-shadow-raised)]"
      onFocusCapture={(event) => {
        if (event.target instanceof HTMLElement) retainedFocus.current = event.target;
      }}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        onRequestClose();
      }}
    >
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-line-soft bg-surface-muted/70 px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <h2 id={titleId} className="text-base font-medium text-ink">
            {title}
          </h2>
          {description ? (
            <p id={descriptionId} className="mt-0.5 text-xs leading-5 text-ink-muted">
              {description}
            </p>
          ) : null}
        </div>
        <IconButton label={t("copy.7b31a9fc4816")} size="sm" onClick={onRequestClose}>
          <X className="size-4" />
        </IconButton>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
        <Form id={formId} onSubmit={onSubmit} noValidate>
          {children}
        </Form>
      </div>
      <footer className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line-soft bg-surface-muted px-4 py-3 sm:px-5">
        {footer}
      </footer>
    </section>
  );
}

/**
 * A canonical record opened without abandoning the surface that led to it.
 *
 * This is deliberately roomier than a create/edit dialog: a Task, Routine,
 * Plan or Span detail may contain history, a timeline and contextual records.
 * It still stops short of becoming a second full-screen page, leaving a clear
 * margin and a visible sense of place around the record. The title bar is kept
 * quiet because the record itself owns the real page heading and actions.
 */
export function RecordDetailDialog({
  open,
  onClose,
  title,
  kind = t("copy.8738e52615b2"),
  children,
  size = "xl",
  minimizable = false,
  dirty = false,
  pending = false,
  closeControllerRef,
  activation = 0,
  layerOrder = 0,
  onActivate,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  kind?: string;
  children: ReactNode;
  size?: "lg" | "xl";
  /**
   * Lets the record be put aside without losing it.
   *
   * A detail opened over its source is often consulted *while* doing something
   * else. Minimising keeps the record mounted and returns it to the shell dock;
   * only the close control ends it.
   */
  minimizable?: boolean;
  dirty?: boolean;
  pending?: boolean;
  closeControllerRef?: Ref<RecordDialogCloseController>;
  /** Reopening the same global record restores a minimized window. */
  activation?: number;
  layerOrder?: number;
  onActivate?: () => void;
}) {
  useLocale();
  const overlayDepth = useContext(OverlayDepthContext);
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId("record-detail-title");
  const windowId = useId("record-window");
  const [minimized, setMinimized] = useState(false);
  const dockApi = useWindowDock();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const afterCloseRef = useRef<(() => void) | null>(null);
  const editors = useRef(new Map<string, () => RecordDialogCloseController>());
  const registerEditor = useCallback<DetailEditorRegistration>((id, editor) => {
    if (editor) editors.current.set(id, editor);
    else editors.current.delete(id);
  }, []);
  const requestClose = useCallback(
    (afterClose?: () => void) => {
      if (pending) return;
      setMinimized(false);
      const editor = [...editors.current.values()]
        .reverse()
        .map((read) => read())
        .find((entry) => entry.open);
      if (editor) {
        editor.requestClose(() => {
          onClose();
          afterClose?.();
        });
        return;
      }
      if (confirmDiscard) {
        afterCloseRef.current = null;
        setConfirmDiscard(false);
        return;
      }
      if (dirty) {
        afterCloseRef.current = afterClose ?? null;
        setConfirmDiscard(true);
        return;
      }
      onClose();
      afterClose?.();
    },
    [confirmDiscard, dirty, onClose, pending],
  );
  useImperativeHandle(closeControllerRef, () => ({ open, dirty, requestClose }), [
    open,
    dirty,
    requestClose,
  ]);
  const undock = dockApi?.undock;
  useEffect(() => () => undock?.(windowId), [undock, windowId]);
  useEffect(() => {
    if (activation > 0) setMinimized(false);
  }, [activation]);
  const canMinimize = minimizable && dockApi !== null;
  const ownerVisible = useOverlayVisibility();
  const depth = Math.max(overlayDepth, layerOrder * 10);
  const visible = open && ownerVisible && !(canMinimize && minimized);

  useEffect(() => {
    if (!open) setMinimized(false);
  }, [open]);

  useDockedWindow(
    open && canMinimize && minimized
      ? {
          id: windowId,
          kind,
          title,
          onRestore: () => {
            setMinimized(false);
            onActivate?.();
          },
          onClose: () => requestClose(),
        }
      : null,
  );

  useFocusTrap(panel, visible);
  useScrollLock(visible);
  useEscape(() => requestClose(), visible, panel, depth);

  const layer = (
    <AnimatePresence>
      {open && (
        <div
          hidden={!visible}
          inert={!visible}
          style={{ display: visible ? undefined : "none", zIndex: 50 + depth }}
          className="cx-overlay-viewport fixed z-50 flex items-center justify-center p-3 sm:p-4 lg:p-6"
        >
          <motion.div
            {...BACKDROP}
            className="absolute inset-0 bg-overlay/90 backdrop-blur-[3px]"
            onClick={() => requestClose()}
          />
          <motion.section
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ opacity: 0, y: 16, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.99 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className={clsx(
              "relative flex max-h-[calc(var(--ex-viewport-height)*0.94)] w-full flex-col overflow-hidden rounded-[1.35rem] border border-line bg-surface-raised shadow-[var(--ex-shadow-overlay)]",
              size === "xl" ? "max-w-6xl" : "max-w-4xl",
            )}
          >
            <header className="flex min-h-12 shrink-0 items-center justify-between gap-4 border-b border-line-soft bg-surface-muted/70 px-4 sm:px-5">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="size-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-2xs font-medium tracking-[0.08em] text-ink-faint uppercase">
                    {kind}
                  </p>
                  <span id={titleId} className="sr-only">
                    {title}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                {canMinimize ? (
                  <IconButton
                    label={t("copy.e462ec557398")}
                    size="sm"
                    onClick={() => setMinimized(true)}
                  >
                    <Minus className="size-4" />
                  </IconButton>
                ) : null}
                <IconButton label={t("copy.40a89eeb71ab")} size="sm" onClick={() => requestClose()}>
                  <X className="size-4" />
                </IconButton>
              </div>
            </header>

            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-surface"
              data-record-detail-scroll
            >
              <RecordDetailActionsContext.Provider
                value={{
                  minimize: () => {
                    if (canMinimize) setMinimized(true);
                    else requestClose();
                  },
                  requestClose: () => requestClose(),
                }}
              >
                <DetailEditorContext.Provider value={registerEditor}>
                  {children}
                </DetailEditorContext.Provider>
              </RecordDetailActionsContext.Provider>
            </div>
            {confirmDiscard && (
              <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line-soft bg-surface-muted px-4 py-3">
                <p className="text-sm text-caution" role="status">
                  {t("copy.5914f47999f8")}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      afterCloseRef.current = null;
                      setConfirmDiscard(false);
                    }}
                  >
                    {t("copy.7b294c635e76")}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      const afterClose = afterCloseRef.current;
                      afterCloseRef.current = null;
                      setConfirmDiscard(false);
                      onClose();
                      afterClose?.();
                    }}
                  >
                    {t("copy.7696d78c4ec1")}
                  </Button>
                </div>
              </footer>
            )}
          </motion.section>
        </div>
      )}
    </AnimatePresence>
  );

  return portalLayer(layer, visible, depth);
}

/**
 * A side sheet.
 *
 * The detail surface for anything you want to edit *without* losing the list
 * behind it — which on a wide screen is most things. It slides from the right
 * on desktop and from the bottom on a phone, because that is where a thumb is.
 */
export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: "sm" | "md" | "lg";
}) {
  useLocale();
  const overlayDepth = useContext(OverlayDepthContext);
  const panel = useRef<HTMLDivElement>(null);
  const visible = useOverlayVisibility() && open;
  useFocusTrap(panel, visible);
  useScrollLock(visible);
  useEscape(onClose, visible, panel, overlayDepth);

  // A dialog without an accessible name is announced as just "dialog". The
  // heading is already on screen and may be rich content, so point at it rather
  // than duplicating it into an aria-label.
  const titleId = useId("sheet-title");

  const size = width === "sm" ? "sm:max-w-md" : width === "lg" ? "sm:max-w-2xl" : "sm:max-w-lg";

  /**
   * A drawer on a desktop, a bottom sheet on a phone.
   *
   * The same panel used to slide in from the right at every width, which is a
   * mouse gesture performed on a touch device: it arrives from the edge furthest
   * from the thumb, and its primary action lands at the bottom of a full-height
   * surface that the keyboard then covers. On a phone it now rises from the
   * bottom, stops short of the top so the page behind stays visible — you are
   * editing a thing, not navigating away from it — and keeps its footer above
   * the safe area.
   */
  const phone = !useMediaQuery("(min-width: 40rem)");

  const layer = (
    <AnimatePresence>
      {open && (
        <div
          hidden={!visible}
          inert={!visible}
          style={{ display: visible ? undefined : "none", zIndex: 50 + overlayDepth }}
          className={clsx(
            "cx-overlay-viewport fixed z-50 flex",
            phone ? "items-end" : "justify-end",
          )}
        >
          <motion.div {...BACKDROP} className="absolute inset-0 bg-overlay" onClick={onClose} />
          <motion.aside
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={phone ? { y: "100%" } : { x: "100%" }}
            animate={phone ? { y: 0 } : { x: 0 }}
            exit={phone ? { y: "100%" } : { x: "100%" }}
            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
            className={clsx(
              "relative flex w-full flex-col bg-surface shadow-[var(--ex-shadow-overlay)]",
              phone
                ? "max-h-[calc(var(--ex-viewport-height)*0.92)] rounded-t-2xl border-t border-line"
                : "h-[var(--ex-viewport-height)] border-l border-line",
              size,
            )}
          >
            {phone && (
              // The one affordance that says "this came up from the bottom and
              // can go back down".
              <span
                aria-hidden="true"
                className="mx-auto mt-2.5 h-1 w-9 shrink-0 rounded-full bg-line-strong"
              />
            )}

            <header
              className={clsx(
                "flex items-start justify-between gap-4 border-b border-line-soft px-5",
                phone ? "py-3" : "py-4",
              )}
            >
              <div className="min-w-0">
                <h2 id={titleId} className="truncate text-base font-medium text-ink">
                  {title}
                </h2>
                {subtitle && <div className="mt-0.5 text-xs text-ink-faint">{subtitle}</div>}
              </div>
              <IconButton label={t("copy.7b31a9fc4816")} size="sm" onClick={onClose}>
                <X className="size-4" />
              </IconButton>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

            {footer && (
              /* Pinned, and clear of the home indicator. The save action is the
                 reason the sheet exists; it does not scroll away. */
              <footer className="flex shrink-0 items-center gap-2 border-t border-line-soft bg-surface-muted px-5 py-3">
                {footer}
              </footer>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );

  return portalLayer(layer, visible, overlayDepth);
}

/** A popover anchored to its trigger. Closes on Escape or an outside click. */
export function Popover({
  trigger,
  children,
  align = "start",
  className,
  panelClassName,
  panelRole = "menu",
}: {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  children: (props: { close: () => void }) => ReactNode;
  align?: "start" | "end";
  className?: string;
  panelClassName?: string;
  panelRole?: "menu" | "dialog";
}) {
  useLocale();
  const [open, setOpen] = useState(false);
  const visible = useOverlayVisibility() && open;
  const container = useRef<HTMLDivElement>(null);
  const overlayDepth = useContext(OverlayDepthContext);
  const panel = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0, maxHeight: 480 });
  const triggerFocus = useRef<HTMLElement | null>(null);
  const panelId = useId("popover-menu");
  const generatedTriggerId = useId("popover-trigger");
  useEffect(() => {
    if (!visible) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (container.current?.contains(target) || panel.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [visible]);

  // Menus in clipped lists belong above the list and shell, just like dialogs.
  // Measure the safe overlay viewport so native insets and the virtual keyboard
  // participate in placement. Flip above the trigger when there is more room.
  useLayoutEffect(() => {
    if (!visible) return;
    let frame = 0;
    const place = () => {
      const anchor = container.current?.getBoundingClientRect();
      const bounds = viewport.current?.getBoundingClientRect();
      const menu = panel.current;
      if (!anchor || !bounds || !menu) return;
      const visual = window.visualViewport;
      const topEdge = Math.max(bounds.top, visual?.offsetTop ?? 0) + 8;
      const bottomEdge =
        Math.min(bounds.bottom, (visual?.offsetTop ?? 0) + (visual?.height ?? innerHeight)) - 8;
      const below = Math.max(0, bottomEdge - anchor.bottom - 6);
      const above = Math.max(0, anchor.top - topEdge - 6);
      const height = menu.scrollHeight + 2;
      const flip = height > below && above > below;
      const maxHeight = Math.max(48, flip ? above : below);
      const top = flip ? anchor.top - 6 - Math.min(height, maxHeight) : anchor.bottom + 6;
      const start = align === "end" ? anchor.right - menu.offsetWidth : anchor.left;
      const left = Math.max(bounds.left + 8, Math.min(start, bounds.right - menu.offsetWidth - 8));
      const next = {
        left: left - bounds.left,
        top: Math.max(topEdge, top) - bounds.top,
        maxHeight,
      };
      setPosition((current) =>
        current.left === next.left &&
        current.top === next.top &&
        current.maxHeight === next.maxHeight
          ? current
          : next,
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    place();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    if (panel.current) observer?.observe(panel.current);
    if (container.current) observer?.observe(container.current);
    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, true);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, true);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [visible, align]);

  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) {
      const target = triggerFocus.current;
      const closingPanel = panel.current;
      requestAnimationFrame(() => {
        // A subsequent click/key may already have moved focus somewhere useful.
        if (
          document.activeElement === document.body ||
          closingPanel?.contains(document.activeElement)
        )
          target?.focus();
      });
    }
  };

  useEscape(() => close(true), visible, panel, overlayDepth);

  const items = () =>
    panel.current
      ? [...panel.current.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])')]
      : [];

  /**
   * A menu you can drive without a mouse.
   *
   * Tab stepped through these already, because they are real buttons — but a
   * menu is not a form, and a person who opens one expects the arrow keys to
   * work and expects the first item to be focused when it appears. Tabbing out
   * of the last item also used to walk into the page behind the open panel.
   */
  const focusAt = (index: number) => {
    const list = items();
    if (list.length === 0) return;
    const wrapped = (index + list.length) % list.length;
    list[wrapped]?.focus();
  };

  useEffect(() => {
    if (!visible) return;
    const frame = requestAnimationFrame(() => {
      const selector =
        panelRole === "menu"
          ? '[role="menuitem"]:not([disabled])'
          : 'input:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
      panel.current?.querySelector<HTMLElement>(selector)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [visible, panelRole]);

  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    if (panelRole !== "menu") return;
    const list = items();
    if (list.length === 0) return;
    const current = list.indexOf(document.activeElement as HTMLElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusAt(current + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusAt(current - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusAt(list.length - 1);
    } else if (event.key === "Tab") {
      // Leaving the menu closes it, rather than moving focus behind an open
      // panel that is still covering what it lands on.
      event.preventDefault();
      close(true);
    }
  };

  const toggle = () => {
    if (!open) triggerFocus.current = document.activeElement as HTMLElement | null;
    setOpen((value) => !value);
  };
  const rawTrigger = trigger({ open, toggle });
  const triggerElement = isValidElement(rawTrigger)
    ? (rawTrigger as ReactElement<Record<string, unknown> & { id?: string }>)
    : null;
  const triggerId = triggerElement?.props.id ?? generatedTriggerId;
  const namedTrigger = triggerElement
    ? cloneElement(triggerElement, {
        id: triggerId,
        "aria-haspopup": panelRole,
        "aria-expanded": open,
        "aria-controls": panelId,
      })
    : rawTrigger;

  return (
    <div ref={container} className={clsx("relative", className)}>
      {namedTrigger}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <div
                ref={viewport}
                hidden={!visible}
                inert={!visible}
                style={{
                  display: visible ? undefined : "none",
                  zIndex: Math.max(60, 50 + overlayDepth),
                }}
                className="cx-overlay-viewport pointer-events-none fixed z-[60]"
              >
                <motion.div
                  ref={panel}
                  id={panelId}
                  role={panelRole}
                  aria-labelledby={triggerId}
                  initial={{ opacity: 0, y: -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={{ duration: 0.14, ease: [0.22, 1, 0.36, 1] }}
                  onKeyDown={onPanelKeyDown}
                  style={position}
                  className={clsx(
                    "pointer-events-auto absolute max-w-[min(18rem,calc(100vw-2rem))] min-w-52 overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg border border-line",
                    "bg-surface-raised p-1 shadow-[var(--ex-shadow-overlay)]",
                    panelClassName,
                  )}
                >
                  <EscapeLayerBoundary>
                    {children({ close: () => close(true) })}
                  </EscapeLayerBoundary>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}

export function MenuItem({
  icon,
  children,
  onClick,
  danger,
  disabled,
  shortcut,
}: {
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  shortcut?: string;
}) {
  useLocale();
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
        "[@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:py-2.5",
        danger ? "text-critical hover:bg-critical-soft" : "text-ink hover:bg-surface-muted",
        disabled && "cursor-not-allowed opacity-45 hover:bg-transparent",
      )}
    >
      {icon && <span className="shrink-0 text-ink-faint">{icon}</span>}
      <span className="min-w-0 flex-1 break-words">{children}</span>
      {shortcut && <Kbd>{shortcut}</Kbd>}
    </button>
  );
}

export function MenuSeparator() {
  useLocale();
  return <hr className="my-1 h-px border-0 bg-line-soft" />;
}

export function Kbd({ children }: { children: ReactNode }) {
  useLocale();
  return (
    <kbd className="rounded border border-line bg-bg-sunken px-1.5 py-0.5 font-mono text-[10px] font-medium text-ink-faint">
      {children}
    </kbd>
  );
}

/**
 * A tooltip.
 *
 * Deliberately hover- and focus-only, and deliberately never the only place a
 * piece of information lives. A tooltip is a reminder for someone who already
 * knows, not documentation for someone who does not.
 */
export function Tooltip({
  content,
  children,
  side = "top",
}: {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom";
}) {
  useLocale();
  const [open, setOpen] = useState(false);
  const timer = useRef<number>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const show = () => {
    timer.current = window.setTimeout(() => setOpen(true), 350);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
  };

  return (
    // The handlers belong to the tooltip, not to the child: this span is a
    // positioning context that happens to observe hover and focus. Giving it an
    // interactive role would announce a control that does not exist.
    // biome-ignore lint/a11y/noStaticElementInteractions: wrapper, not a control
    <span
      className="relative inline-flex"
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocus={() => setOpen(true)}
      onBlur={hide}
    >
      {children}
      <AnimatePresence>
        {open && (
          <motion.span
            role="tooltip"
            initial={{ opacity: 0, y: side === "top" ? 3 : -3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className={clsx(
              "pointer-events-none absolute left-1/2 z-50 w-max max-w-56 -translate-x-1/2 rounded-md",
              "border border-line bg-surface-raised px-2 py-1 text-xs text-ink shadow-[var(--ex-shadow-overlay)]",
              side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
            )}
          >
            {content}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
